import { HotelDirectOffers } from './HotelDirectOffers';
import { HotelCancellation, cancellationBoundary, subscribeCancellationBoundary } from './HotelCancellation';
import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { HotelBoardLabel } from './HotelConditionLabel';
import { compareHotelAmounts, formatHotelAmount } from './searchModel';
import { useHotelExpansion } from './useHotelExpansion';
import type { HotelPublicContent } from '@bandido/shared/types/hotels/hotel-content';
import { HotelPublishedContent, hotelContentDisplayName } from './HotelPublishedContent';
import { useHotelContentDetail } from './hotelContentFlow';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  HotelResultCard,
  HotelRateMinimum,
  HotelResultGroup,
  HotelResultMeta,
} from '@bandido/shared/types/hotels/hotel-search-results';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import type { HotelFare, HotelSummary } from './searchModel';
import type { HotelSearchTransport } from './hotelSearchFlow';
export function HotelGroupSummary({ group }: { group: HotelResultGroup }) {
  const { t } = useTranslation(),
    s = group.summary,
    m = group.minimum_reported_price;
  return (
    <Stack gap={0.5}>
      <Typography variant="subtitle1">
        {m
          ? t('hotelSearch.reportedFrom', { currency: m.price.currency, amount: m.price.amount })
          : t('hotelSearch.minimumUnknown')}
      </Typography>
      <Typography variant="body2">{t('hotelSearch.wholeOffer')}</Typography>
      <Typography variant="body2">
        {s ? s.board_categories.map((c) => t(`hotelSearch.boards.${c}`)).join(' · ') : t('hotelSearch.boards.unknown')}{' '}
        · {t(`hotelSearch.refundability.${s?.refundability ?? 'unknown'}`)}
      </Typography>
      {s?.board_mixed && <Typography variant="body2">{t('hotelSearch.mixedBoards')}</Typography>}
      <Typography variant="caption">
        {t('hotelSearch.priceBasis')}: {t(`hotelSearch.basis.${s?.price_basis ?? 'unknown'}`)} ·{' '}
        {t('hotelSearch.payment')}: {s?.payment_type ?? '—'} · {t('hotelSearch.optionStatus')}:{' '}
        {s?.option_status ?? '—'}
      </Typography>
      <Typography variant="caption">
        {t('hotelSearch.packageCondition')}:{' '}
        {s?.package === true
          ? 'PACKAGE'
          : s?.package === false
            ? t('hotelSearch.notPackage')
            : t('hotelSearch.conditionUnknown')}
      </Typography>
      <Typography variant="caption">
        {t('hotelSearch.reportedCharges', {
          included: s?.charges.included_count ?? 0,
          excluded: s?.charges.excluded_count ?? 0,
          unknown: s?.charges.unknown_count ?? 0,
        })}
      </Typography>
      {(!s || s.charges.details_missing) && (
        <Typography variant="caption" color="warning.main">
          {t('hotelSearch.chargeDetailsMissing')}
        </Typography>
      )}
      {s?.charges.other_currency && <Typography variant="caption">{t('hotelSearch.chargeOtherCurrency')}</Typography>}
      <Typography variant="caption">{t('hotelSearch.navigationGroup')}</Typography>
      <Typography variant="caption">{t('hotelSearch.offerCount', { count: group.fare_count })}</Typography>
      {m && <Typography variant="caption">{t('hotelSearch.observedAt', { time: m.observed_at })}</Typography>}
    </Stack>
  );
}
export function HotelResultEvidence({ meta }: { meta?: HotelResultMeta }) {
  const { t } = useTranslation();
  if (!meta) return null;
  const warnings = [...new Set(meta.warnings.map((w) => w.trim()).filter(Boolean))];
  const incomplete = meta.partial && ['completed', 'failed'].includes(meta.search_status);
  return (
    <Stack gap={1}>
      {(incomplete || warnings.length > 0 || !!meta.unmapped_fare_count) && (
        <Alert severity="warning">
          {t(incomplete ? 'hotelSearch.partialFriendly' : 'hotelSearch.resultsNotice')}
          <Box component="details" sx={{ mt: 0.5 }}>
            <Box component="summary" sx={{ cursor: 'pointer' }}>
              {t('hotelSearch.noticeDetails')}
            </Box>
            {warnings.map((warning) => (
              <Typography variant="caption" display="block" key={warning}>
                {warning}
              </Typography>
            ))}
            {!!meta.unmapped_fare_count && (
              <Typography variant="caption">
                {t('hotelSearch.unmappedOffers', { count: meta.unmapped_fare_count })}
              </Typography>
            )}
          </Box>
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        {t('hotelSearch.receivedPrices')}
      </Typography>
    </Stack>
  );
}
/** A compact price preview only; the original offers and policies remain in detail. */
export function orderHotelRateMinimums(rates: HotelRateMinimum[], now = Date.now()): HotelRateMinimum[] {
  const boards = ['room_only', 'breakfast', 'half_board', 'full_board', 'all_inclusive', 'other', 'unknown'];
  const boardKey = (rate: HotelRateMinimum) =>
    rate.comparison.board_categories
      .map((board) => boards.indexOf(board))
      .sort()
      .join(',');
  const state = (rate: HotelRateMinimum) => {
    const boundary = cancellationBoundary(rate.cancellation);
    return boundary !== null && now >= boundary ? 'unknown' : (rate.cancellation?.state ?? 'unknown');
  };
  const buckets = new Map<string, Map<string, HotelRateMinimum>>();
  for (const rate of rates) {
    const key = JSON.stringify([
      boardKey(rate),
      rate.price.currency,
      rate.comparison.price_basis,
      rate.comparison.payment_type,
      rate.comparison.option_status,
      rate.comparison.binding,
      rate.package,
      rate.charges.snapshot_digest,
      rate.comparison.refundability === 'unknown' ? 'unknown' : 'known',
    ]);
    const bucket = buckets.get(key) ?? new Map<string, HotelRateMinimum>();
    const previous = bucket.get(state(rate));
    if (!previous || compareHotelAmounts(rate.price.amount, previous.price.amount) < 0) {
      bucket.set(state(rate), rate);
    }
    buckets.set(key, bucket);
  }
  return [...buckets.values()]
    .flatMap((bucket) => {
      const free = bucket.get('free_now');
      const charged = bucket.get('penalty_applies');
      return [...bucket.values()].filter(
        (rate) =>
          state(rate) === 'unknown' ||
          ((!free || rate === free || compareHotelAmounts(rate.price.amount, free.price.amount) < 0) &&
            (state(rate) !== 'non_refundable' ||
              !charged ||
              compareHotelAmounts(rate.price.amount, charged.price.amount) < 0)),
      );
    })
    .sort(
      (a, b) =>
        a.price.currency.localeCompare(b.price.currency) ||
        a.comparison.price_basis.localeCompare(b.comparison.price_basis) ||
        compareHotelAmounts(a.price.amount, b.price.amount) ||
        boardKey(a).localeCompare(boardKey(b)),
    );
}
export function HotelResultCards({
  cards,
  hotels,
  onOpen,
  content = {},
  contentLoading = false,
}: {
  content?: Record<string, HotelPublicContent>;
  contentLoading?: boolean;
  cards: HotelResultCard[];
  hotels: HotelSummary[];
  onOpen: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // A free-cancellation deadline can expire even while no search updates arrive.
  const boundaries = useMemo(
    () =>
      cards.flatMap((card) =>
        (card.rate_minimums ?? [])
          .map((rate) => cancellationBoundary(rate.cancellation))
          .filter((boundary): boundary is number => boundary !== null),
      ),
    [cards],
  );
  const subscribe = useCallback(
    (changed: () => void) => {
      const cleanups = boundaries.map((boundary) => subscribeCancellationBoundary(boundary, changed));
      return () => cleanups.forEach((cleanup) => cleanup());
    },
    [boundaries],
  );
  const snapshot = () => boundaries.filter((boundary) => Date.now() >= boundary).length;
  useSyncExternalStore(subscribe, snapshot, snapshot);
  const format = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });
  return (
    <Stack gap={2}>
      {cards.map((c) => {
        const ordered = orderHotelRateMinimums(c.rate_minimums ?? []);
        const groups = new Map<string, HotelRateMinimum[]>();
        for (const rate of ordered) {
          const key = JSON.stringify([
            rate.price.currency,
            rate.comparison.price_basis,
            [...rate.comparison.board_categories].sort(),
          ]);
          groups.set(key, [...(groups.get(key) ?? []), rate]);
        }
        const preview = [...groups.values()].flatMap((rates) => (expanded[c.property_id] ? rates : rates.slice(0, 1)));
        const canExpand = ordered.length > groups.size;
        const h = hotels.find((h) => h.hotel_id === c.property_id);
        return (
          <Card key={c.property_id} data-hotel-id={c.property_id} variant="outlined">
            <CardContent>
              <Stack gap={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="h6">
                      {hotelContentDisplayName(content[c.property_id], h?.display_name ?? c.property_id)}
                    </Typography>
                    <Typography variant="caption">{t('hotelSearch.offerCount', { count: c.fare_count })}</Typography>
                    {c.sort_price !== undefined && (
                      <Typography variant="body2">
                        {c.sort_price
                          ? t('hotelSearch.sortPriceFrom', {
                              amount: formatHotelAmount(c.sort_price.price.amount),
                              currency: c.sort_price.price.currency,
                              basis: t(`hotelSearch.basis.${c.sort_price.price_basis}`),
                            })
                          : t('hotelSearch.noSortPrice')}
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2fr)' },
                    gap: 2,
                    alignItems: 'start',
                  }}
                >
                  <HotelPublishedContent
                    content={content[c.property_id]}
                    loading={contentLoading}
                    name={hotelContentDisplayName(content[c.property_id], h?.display_name ?? c.property_id)}
                    card
                  />
                  <Stack gap={1.5} sx={{ minWidth: 0 }}>
                    <Box
                      component="table"
                      aria-label={t('hotelSearch.rateMinimums')}
                      sx={{
                        width: '100%',
                        tableLayout: 'fixed',
                        '& th:nth-of-type(1)': { width: { xs: '30%', md: '25%' } },
                        '& th:nth-of-type(2)': { width: { xs: '42%', md: '37%' } },
                        '& th:nth-of-type(3)': { width: { xs: '28%', md: '38%' } },
                        borderCollapse: 'collapse',
                        '& th': { textAlign: 'left', bgcolor: 'action.hover', p: 1, fontSize: 12, fontWeight: 500 },
                        '& td': {
                          p: { xs: 0.75, md: 1.5 },
                          borderBottom: 1,
                          borderColor: 'divider',
                          overflowWrap: 'anywhere',
                        },
                      }}
                    >
                      <thead>
                        <tr>
                          <th>{t('hotelSearch.boardFilter')}</th>
                          <th>{t('hotelSearch.cancellationLabel')}</th>
                          <th style={{ textAlign: 'right' }}>{t('hotelSearch.priceFromLabel')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((m) => (
                          <Box component="tr" key={`${m.search_id}:${m.fare_id}`}>
                            <td>
                              <Stack direction="row" gap={0.5} flexWrap="wrap">
                                {m.comparison.board_categories.map((board) => (
                                  <HotelBoardLabel key={board} category={board} />
                                ))}
                              </Stack>
                            </td>
                            <td>
                              <HotelCancellation policy={m.cancellation} compact />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <Stack
                                direction={{ xs: 'column-reverse', md: 'row' }}
                                gap={0.75}
                                alignItems="center"
                                justifyContent="flex-end"
                                sx={{ alignItems: { xs: 'flex-end', md: 'center' } }}
                              >
                                {m.package && (
                                  <Chip
                                    size="small"
                                    label="PACKAGE"
                                    variant="outlined"
                                    sx={{
                                      flexShrink: 0,
                                      height: 18,
                                      fontSize: 10,
                                      color: 'text.secondary',
                                      borderColor: 'divider',
                                      '& .MuiChip-label': { px: 0.75 },
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: { md: 'nowrap' } }}
                                >
                                  {m.price.currency} {formatHotelAmount(m.price.amount)}
                                </Typography>
                              </Stack>
                              {m.comparison.price_basis === 'net' && (
                                <Typography variant="caption">{t('hotelSearch.netPrice')}</Typography>
                              )}
                            </td>
                          </Box>
                        ))}
                      </tbody>
                    </Box>
                    {!c.rate_minimums?.length && (
                      <Typography variant="body2">{t('hotelSearch.minimumUnknown')}</Typography>
                    )}
                    {canExpand && (
                      <Button
                        size="small"
                        aria-expanded={!!expanded[c.property_id]}
                        onClick={() => setExpanded((old) => ({ ...old, [c.property_id]: !old[c.property_id] }))}
                      >
                        {t(expanded[c.property_id] ? 'hotelSearch.hideComparison' : 'hotelSearch.compareCancellation')}
                      </Button>
                    )}
                    {c.has_more_rate_minimums && (
                      <Typography variant="caption">{t('hotelSearch.moreRatesAvailable')}</Typography>
                    )}
                    <Typography variant="caption">{t('hotelSearch.wholeStay')}</Typography>
                  </Stack>
                </Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{ pt: 1.5, borderTop: 1, borderColor: 'divider' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    {content[c.property_id]?.status === 'published' && content[c.property_id]?.location
                      ? [
                          content[c.property_id]?.location?.address,
                          content[c.property_id]?.location?.city,
                          content[c.property_id]?.location?.country_code,
                        ]
                          .filter(Boolean)
                          .join(' · ') || t('hotelSearch.locationUnknown')
                      : t('hotelSearch.locationUnknown')}
                    {h?.distance_km != null ? ` · ${format.format(h.distance_km)} km` : ''}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => onOpen(c.property_id)}
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, flexShrink: 0 }}
                  >
                    {t('hotelSearch.viewRooms')}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
}
export function CollectionFeedback({
  busy,
  error,
  reload,
}: {
  busy: boolean;
  error: 'changed' | 'exhausted' | 'error' | null;
  reload: () => unknown;
}) {
  const { t } = useTranslation();
  return (
    <>
      {busy && <Typography role="status">{t('hotelSearch.loading')}</Typography>}
      {error && (
        <Alert severity={error === 'changed' ? 'info' : 'error'}>
          {t(
            error === 'changed'
              ? 'hotelSearch.resultsChanged'
              : error === 'exhausted'
                ? 'hotelSearch.resultsRefreshStopped'
                : 'hotelSearch.error',
          )}
          {error !== 'changed' && (
            <Button onClick={reload} disabled={busy}>
              {t('hotelSearch.reloadResults')}
            </Button>
          )}
        </Alert>
      )}
    </>
  );
}
export function HotelPropertyDetail({
  searchId,
  hotel,
  filters,
  transport,
  onClose,
  onReserve,
  polling = false,
}: {
  searchId: string;
  hotel: HotelSummary;
  filters: HotelSearchFilters;
  transport: HotelSearchTransport;
  onClose: () => void;
  onReserve: (fare: HotelFare) => void;
  polling?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const content = useHotelContentDetail(searchId, hotel.hotel_id, i18n.language, transport);
  const expansion = useHotelExpansion(searchId, hotel.hotel_id, transport);
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle>
        {hotelContentDisplayName(content.data, hotel.display_name)}
        <Button onClick={onClose}>{t('hotelSearch.backResults')}</Button>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2}>
          {content.error && (
            <Alert severity="warning">
              {t('hotelSearch.contentError')}
              <Button onClick={content.reload}>{t('hotelSearch.reloadContent')}</Button>
            </Alert>
          )}
          <HotelPublishedContent
            content={content.data}
            loading={content.loading}
            name={hotelContentDisplayName(content.data, hotel.display_name)}
            detail
          />
          {expansion.error && <Alert severity="warning">{t('hotelSearch.expansionError')}</Alert>}
          {expansion.state && ['accepted', 'running'].includes(expansion.state.status) && (
            <Typography role="status">{t('hotelSearch.loadingOffers')}</Typography>
          )}
          <HotelDirectOffers
            searchId={searchId}
            hotel={hotel}
            filters={filters}
            transport={transport}
            onReserve={onReserve}
            polling={polling}
            detailId={expansion.state?.detail_search_id}
            detailPolling={!!expansion.state && ['accepted', 'running'].includes(expansion.state.status)}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
